/**
 * Reglas centralizadas de disponibilidad y clasificación de tipos de endoso
 * de cancelación (51, 52, 53, 54), del tipo 55 (Cancelación Masiva) y del
 * tipo 25 (Actualización datos fiscales: Régimen y CP receptor).
 *
 * No se hardcodea el catálogo completo de tipos de endoso (viene del backend
 * vía endoso.listType). Solo se centraliza:
 *  - qué tipos siguen el flujo de cancelación (comparte el ciclo del tipo 5)
 *  - qué ramos habilitan el tipo 55
 *  - los acrónimos y la regla de completitud del tipo 25
 */

export const CANCELACION_TYPES = [5, 51, 52, 53, 54];
export const CANCELACION_SUBREASON_TYPES = [5];
export const TIPO_CANCELACION_MASIVA = 55;
export const RAMOS_CANCELACION_MASIVA = [201, 202, 203, 204, 205, 211];

/* Datos fiscales (HU6) */
export const TIPO_DATOS_FISCALES = 25;

/**
 * Acrónimos con los que poliza/configuracion devuelve los campos editables del
 * tipo 25 dentro de datosEndoso. Son la llave de lectura en dataSend, así que
 * viven aquí y no repartidos por los componentes (ADR-003: contrato backend).
 */
export const ACRONIMO_CP_RECEPTOR = "CP";
export const ACRONIMO_REGIMEN_FISCAL = "IDREGFIS";

/** CP del receptor: valor numérico de 5 dígitos (regla de negocio HU6). */
export const CP_RECEPTOR_LENGTH = 5;

export const isCancelacionType = (tipend) =>
  CANCELACION_TYPES.includes(Number(tipend));

export const requiresCancelacionSubreason = (tipend) =>
  CANCELACION_SUBREASON_TYPES.includes(Number(tipend));

export const isCancelacionMasivaAllowed = (ramo) =>
  RAMOS_CANCELACION_MASIVA.includes(Number(ramo));

export const isTipoEndosoAvailable = (idEndoso, ramo) =>
  Number(idEndoso) !== TIPO_CANCELACION_MASIVA ||
  isCancelacionMasivaAllowed(ramo);

export const isDatosFiscalesType = (tipend) =>
  Number(tipend) === TIPO_DATOS_FISCALES;

export const isCpReceptorValido = (codigoPostal) =>
  new RegExp(`^\\d{${CP_RECEPTOR_LENGTH}}$`).test(String(codigoPostal ?? ""));

/**
 * El tipo 25 solo puede guardar borrador con los dos campos capturados: CP del
 * receptor de 5 dígitos y un régimen fiscal seleccionado del catálogo.
 */
export const isDatosFiscalesCompleto = (dataSend = {}) =>
  isCpReceptorValido(dataSend[ACRONIMO_CP_RECEPTOR]) &&
  Boolean(dataSend[ACRONIMO_REGIMEN_FISCAL]);

/* Refacturación (HU6, iteración 2) */

/** Tamaño de página del GET poliza/lista-recibos. */
export const TAMANIO_PAGINA_RECIBOS = 5;

/** Texto literal mostrado al confirmar la refacturación (HU6 paso 3). */
export const MENSAJE_REFACTURACION_OK =
  "A partir del cambio las facturas posteriores tomarán esta información actualizada";

/* Reintento de refacturación (HU6 - reintentos) */

/** Modal tras cualquier error en el primer intento. */
export const MENSAJE_REINTENTO_REFACTURACION =
  "Ocurrió un error en algunos recibos ¿Desea volver a intentar?";

/** Toast cuando el reintento vuelve a fallar. */
export const MENSAJE_REFACTURACION_CANAL_TRADICIONAL =
  "Algunas facturas no fueron posibles de refacturar. Comuníquese con canal tradicional";

const getMotanu = (datosEndoso) =>
  datosEndoso?.find((item) => item.acronimo === "MOTANU");

// Si /configuracion devuelve una sola opcion de Motivo de anulacion (MOTANU)
// para un tipo de cancelacion, retorna su id para mostrar el resumen sin
// pasar por el formulario. En cualquier otro caso retorna null.
export const getMotivoAnulacionUnico = (tipend, datosEndoso) => {
  if (!isCancelacionType(tipend)) return null;
  const opciones = getMotanu(datosEndoso)?.catalogo?.catalogo;
  return opciones?.length === 1 ? opciones[0].id : null;
};

// Motivo con el que se puede mostrar el resumen de una cancelacion:
// - unico: el catalogo trae una sola opcion (el guarda se hace al confirmar)
// - guardado: borrador retomado que ya tiene el motivo guardado en backend
// Retorna null si hay que mostrar el formulario.
export const getMotivoResumenCancelacion = (tipend, datosEndoso) => {
  const unico = getMotivoAnulacionUnico(tipend, datosEndoso);
  if (unico !== null) return { motivo: unico, unico: true };
  const guardado = getMotanu(datosEndoso)?.valorActual;
  if (isCancelacionType(tipend) && guardado)
    return { motivo: guardado, unico: false };
  return null;
};
